import React, { useState } from 'react';
import { Modal, Table } from '@mantine/core';
import type { ReportStationaryStationSummary } from '../../../../services/reports/reports.service';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency } from '../../shared/utils/dashboard-page.utils';
import type { SectionKey } from '../../shared/types/dashboard-page.types';
import classes from './stationary-stations-section.module.scss';

type StationaryStationsSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  summaries: ReportStationaryStationSummary[];
  dateText: string;
};

export const StationaryStationsSection: React.FC<StationaryStationsSectionProps> = ({
  collapsedSections,
  toggleSection,
  summaries,
  dateText,
}) => {
  const [selectedStation, setSelectedStation] = useState<ReportStationaryStationSummary | null>(null);

  return (
    <>
      <SectionCard
        title="עמדות נייחות"
        sectionKey="stationaryStations"
        collapsed={collapsedSections.stationaryStations}
        onToggle={() => toggleSection('stationaryStations')}
      >
        {summaries.length === 0 ? (
          <p className={classes.emptyHint}>אין נתונים להצגה</p>
        ) : (
          <div className={classes.list}>
            {summaries.map((summary) => (
              <button
                key={summary.stationaryName}
                type="button"
                className={classes.card}
                onClick={() => setSelectedStation(summary)}
                data-testid={`dashboard-stationary-station-${summary.stationaryName}`}
              >
                <span className={classes.date}>{dateText}</span>
                <span className={classes.cell}>
                  <span className={classes.cellLabel}>שם עמדה</span>
                  <span className={classes.cellValue}>{summary.stationaryName}</span>
                </span>
                <span className={classes.cell}>
                  <span className={classes.cellLabel}>סה״כ מכירות</span>
                  <span className={classes.cellValue}>{asCurrency(summary.salesTotal)}</span>
                </span>
                <span className={classes.cell}>
                  <span className={classes.cellLabel}>סה״כ טיפ</span>
                  <span className={classes.cellValue}>{asCurrency(summary.tipsTotal)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <Modal
        opened={selectedStation !== null}
        onClose={() => setSelectedStation(null)}
        title={selectedStation?.stationaryName ?? ''}
        centered
        dir="rtl"
        data-testid="dashboard-stationary-station-details"
        classNames={{
          content: classes.modalContent,
          header: classes.modalHeader,
          title: classes.modalTitle,
          close: classes.modalClose,
          body: classes.modalBody,
        }}
      >
        {selectedStation ? (
          <Table className={classes.detailsTable}>
            <Table.Tbody>
              {[
                ['סה״כ מכירות', selectedStation.salesTotal],
                ['מכירות אשראי', selectedStation.salesCredit],
                ['מכירות מזומן', selectedStation.salesCash],
                ['סה״כ טיפ', selectedStation.tipsTotal],
                ['טיפ אשראי', selectedStation.tipsCredit],
                ['טיפ מזומן', selectedStation.tipsCash],
              ].map(([label, value]) => (
                <Table.Tr key={label as string}>
                  <Table.Th className={classes.labelCell}>{label}</Table.Th>
                  <Table.Td>{asCurrency(value as number)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
      </Modal>
    </>
  );
};